<template>
  <v-layout>
    <v-main :class="containerState">
      <v-container id="main-container" :fluid="true">
        <!-- Toolbar -->
        <v-app-bar id="upp-bar" color="white" :flat="true" class="px-4">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2" size="large">
              mdi-graph-outline
            </v-icon>
            <div class="text-primary text-h5 font-weight-bold">
              {{ $t("labels.page-title") }}
            </div>
          </div>

          <v-spacer></v-spacer>
          <v-row class="d-flex justify-end align-center">
            <v-col
              cols="12"
              sm="12"
              md="10"
              lg="8"
              xl="6"
              class="d-flex align-center align-self-center"
            >
              <v-text-field
                v-model="search"
                class="mr-4 align-self-center"
                density="compact"
                :loading="false"
                variant="solo"
                type="text"
                :label="$t('labels.search')"
                :append-inner-icon="!search ? 'mdi-magnify' : ''"
                clearable
                single-line
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <!--Button-Dialog-Import-->
          <DialogImport
            :apiUrl="digitalTwinModelsListConfig.api"
            :tenantId="realm"
          ></DialogImport>
          <!--Button-Dialog-Create-->
          <DialogCreate
            :apiUrl="digitalTwinModelsListConfig.api"
            :tenantId="realm"
            :domains="digitalTwinCommonConfig.domains"
          ></DialogCreate>
        </v-app-bar>
        <!-- Table -->
        <v-card tile flat>
          <v-data-table
            v-model="selected"
            :headers="headers"
            :items="models"
            :single-select="singleSelect"
            item-key="key"
            item-value="modelId"
            :sort-by="[{ key: 'modelName', order: 'desc' }]"
            :item-selectable="isSelectable"
            show-select
            :search="search"
            :custom-filter="filterModels"
            :loading="loader"
          >
            <!-- Actions Toolbar over table -->
            <template v-slot:[`header.modelName`]>
              <div class="mb-1 d-flex align-center">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      icon="mdi-refresh"
                      class="d-inline-block"
                      variant="plain"
                      color="secondary"
                      v-bind="props"
                      @click="getModelsList()"
                    ></v-icon>
                  </template>
                  <span>{{ $t("labels.refresh") }}</span>
                </v-tooltip>

                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      icon="mdi-delete"
                      :class="showDeleteIcon"
                      class="ml-2"
                      variant="plain"
                      color="secondary"
                      v-bind="props"
                      @click="deleteMultipleModel()"
                    ></v-icon>
                  </template>
                  <span>{{ $t("labels.delete") }}</span>
                </v-tooltip>
              </div>
            </template>
            <!--END Actions Toolbar over table -->

            <!-- NO DATA template -->
            <template v-slot:[`no-data`]>
              <div class="d-flex flex-column justify-center align-center">
                <img
                  :src="
                    digitalTwinModelsListConfig.storageImgs +
                    '/commons/watermark-comic.png'
                  "
                  width="200"
                  alt="No Data"
                />
                <p
                  class="text-black font-weight-bold text-h6 text-uppercase mb-3"
                >
                  {{ $t("strings.no-model") }}
                </p>
                <p>{{ $t("strings.please-add-model") }}</p>
              </div>
            </template>

            <!-- Table's row definition -->
            <template v-slot:[`item.modelName`]="{ item }">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-list-item
                    class="px-0 py-2 no-highlight"
                    v-bind="props"
                    @click="getModelById(item.modelId)"
                  >
                    <v-list-item-title class="font-weight-medium">
                      <span
                        class="d-inline-block text-truncate"
                        style="max-width: 50rem"
                      >
                        {{ item.modelName }}
                      </span>
                    </v-list-item-title>
                    <v-tooltip location="bottom" color="info" :disabled="!item.description">
                      <template v-slot:activator="{ props }">
                        <v-list-item-title
                          class="d-inline-block text-truncate"
                          v-bind="props"
                        >
                          <v-chip
                            class="mr-8 text-white"
                            size="x-small"
                            label
                            variant="elevated"
                            :color="isOwner(item) ? 'primary' : '#3d8ecc'"
                          >
                            <span v-if="isOwner(item)">
                              {{ $t("labels.owner") }}
                            </span>
                            <span v-else>{{ isWriter(item) ? $t("labels.editor") : $t("labels.reader") }}</span>
                          </v-chip>
                          <span
                            class="text-medium-emphasis text-body-2 text-grey"
                            style="max-width: 50rem"
                            >{{ item.description }}</span
                          >
                        </v-list-item-title>
                      </template>
                      <span>{{ item.description }}</span>
                    </v-tooltip>
                  </v-list-item>
                </template>
                <span>{{ $t("labels.open") }} {{ item.modelName }}</span>
              </v-tooltip>
            </template>
            <!-- Status -->
            <template v-slot:[`item.status`]="{ item }">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-if="item.active"
                    v-bind="props"
                    size="large"
                    color="primary"
                    >mdi-cloud-check-outline</v-icon
                  >
                  <v-icon v-else v-bind="props" size="large" color="primary"
                    >mdi-cloud-off-outline</v-icon
                  >
                </template>
                <span>{{
                  $t(
                    "strings." +
                      (item.active ? "publish-status" : "not-publish-status"),
                  )
                }}</span>
              </v-tooltip>
            </template>
            <!-- Validation -->
            <template v-slot:[`item.validation`]="{ item }">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-if="item.hasOutdatedEntities"
                    v-bind="props"
                    size="large"
                    color="red"
                    >mdi-alert-outline</v-icon
                  >
                  <v-icon v-else v-bind="props" size="large" color="green"
                    >mdi-check-circle-outline</v-icon
                  >
                </template>
                <span>{{
                  $t(
                    "strings." +
                      (item.hasOutdatedEntities
                        ? "validation-not-ok"
                        : "validation-ok"),
                  )
                }}</span>
              </v-tooltip>
            </template>
            <!-- Domain -->
            <template v-slot:[`item.domain`]="{ item }">
              <v-list-item
                class="px-0"
                :ripple="false"
                active-class="no-highlight"
              >
                <v-list-item-title class="font-weight-medium">
                  {{ capitalizeFirstLetter(item.domain) }}
                </v-list-item-title>
              </v-list-item>
            </template>
            <!-- Actions -->
            <template v-slot:[`item.actions`]="{ item }">
              <v-spacer></v-spacer>
              <v-menu location="bottom left">
                <template v-slot:activator="{ props }">
                  <v-btn variant="text" icon v-bind="props">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item disabled>
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-form-textbox</v-icon>
                    </template>
                    <v-list-item-title>
                      {{ $t("labels.rename") }}
                    </v-list-item-title>
                  </v-list-item>
                  <!--Button-Dialog-Share-->
                  <DialogShare
                    :apiUrl="digitalTwinModelsListConfig.api"
                    :tenantId="realm"
                    :item="item"
                    :isOwner="isOwner(item)"
                    @openSnackbar="snackbarEvent"
                  ></DialogShare>
                  <v-list-item disabled>
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-content-duplicate</v-icon>
                    </template>
                    <v-list-item-title>
                      {{ $t("labels.duplicate") }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item disabled>
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-download</v-icon>
                    </template>
                    <v-list-item-title>
                      {{ $t("labels.export") }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="!isOwner(item)"
                    @click="deleteModel(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-delete</v-icon>
                    </template>
                    <v-list-item-title>{{
                      $t("labels.delete")
                    }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <!-- END Table's row definition -->
          </v-data-table>
        </v-card>
      </v-container>
    </v-main>
    <!--Snackbar-->
    <v-snackbar v-model="snackbar" top color="green" timeout="5000">
      {{ snackbarText }}
      <template v-slot:actions="{ attrs }">
        <v-btn
          color="white"
          variant="text"
          v-bind="attrs"
          @click="snackbar = false"
        >
          OK
        </v-btn>
      </template>
    </v-snackbar>
    <DialogDelete
      v-model="dialogDelete"
      :isMultipleItem="isMultipleItem"
      @confirm="deleteModelConfirm()"
    ></DialogDelete>
  </v-layout>
</template>

<script>
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";
import version from "../version";
import AppMixin from "@/mixins/AppMixin.js";
import DialogCreate from "@/components/DialogCreate.vue";
import DialogImport from "@/components/DialogImport.vue";
import DialogDelete from "@/components/DialogDelete.vue";
import DialogShare from "@/components/DialogShare.vue";
export default {
  name: "DigitalTwinsList",
  components: {
    DialogCreate,
    DialogImport,
    DialogDelete,
    DialogShare,
  },
  mixins: [utils, events, AppMixin],
  props: ["digitalTwinCommonConfig", "digitalTwinModelsListConfig", "realm"],
  data: function () {
    return {
      appVersion: version,
      loader: false,
      containerState: "expanded",
      search: "",
      models: [],
      selected: [],
      singleSelect: false,
      showDeleteIcon: "d-none",
      snackbar: false,
      snackbarText: "",
      dialogDelete: false,
      editedItem: undefined,
      isMultipleItem: false,
    };
  },
  mounted() {
    console.log("Mounted demf-dtm-models-list v", this.appVersion);

    document.addEventListener(
      "language-localstorage-changed",
      this.languageLocalstorageChanged,
    );

    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener("sidenav-localstorage-changed", (event) => {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    });
  },
  beforeUnmount() {
    document.removeEventListener(
      "language-localstorage-changed",
      this.languageLocalstorageChanged,
    );
  },
  watch: {
    selected() {
      this.showDeleteIcon =
        this.selected.length > 0 ? "d-inline-block" : "d-none";
    },
  },
  computed: {
    headers() {
      return [
        {
          title: "",
          align: "start",
          sortable: false,
          key: "modelName",
          width: "35%",
        },
        {
          title: this.$t("labels.status"),
          align: "start",
          sortable: false,
          key: "status",
          width: "20%",
        },
        {
          title: this.$t("labels.validation"),
          align: "start",
          sortable: false,
          key: "validation",
          width: "20%",
        },
        {
          title: this.$t("labels.domain"),
          align: "start",
          sortable: false,
          key: "domain",
          width: "20%",
        },
        {
          title: "",
          align: "end",
          sortable: false,
          key: "actions",
          width: "5%",
        },
      ];
    },
  },
  methods: {
    filterModels: (value, search, item) => {
      if (!search) return true;
      return item.raw.modelName.toLowerCase().includes(search.toLowerCase());
    },
    languageLocalstorageChanged(event) {
      this.setLocale(event.detail.storage);
    },
    capitalizeFirstLetter(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    isSelectable(item) {
      return !!item?.permissions?.ownership;
    },
    isOwner(item) {
      return !!item?.permissions?.ownership;
    },
    isWriter(item) {
      return !!item?.permissions?.capabilities?.canWrite;
    }
  },
};
</script>

<style scoped>
main.expanded {
  margin-left: 56px;
}
main.expanded header#upp-bar {
  margin-left: 56px;
  width: calc(100% - 56px) !important;
}
main.reduced {
  margin-left: 256px;
}
main.reduced header#upp-bar {
  margin-left: 256px;
  width: calc(100% - 256px) !important;
}
#main-container {
  height: calc(100vh - 104px);
  overflow-y: auto;
  background-color: white;
}

.theme--light.v-list-item:hover::before {
  opacity: 0;
}
.no-highlight.v-list-item:hover::before {
  opacity: 0;
}
.no-highlight.v-list-item--active:hover::before,
.no-highlight.v-list-item--active::before {
  opacity: 0;
}

.outlinedStyle :deep(.v-chip__content) {
  min-width: 6px;
  padding-left: 2px;
  padding-right: 2px;
}
</style>
